import { EFinancingProgram } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import { ControlledStringDateOnlyPicker } from '@if/mui'
import { Divider, Grid, Typography } from '@mui/material'
import { EditAutoRslaWorksheetDto, EditAutoRslaWorksheetDtoSchema } from '@src/data/types/AutoRslaWorksheet'
import { useEffect, useMemo } from 'react'
import {
  Control,
  FieldError,
  FieldErrorsImpl,
  Merge,
  UseFormRegister,
  UseFormReset,
  UseFormWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { InputTextField, SelectComponent } from '../../../components'
import { Constants, CreditApplication, SelectValueListItem } from '../../../data/types'

interface Props {
  control: Control<EditAutoRslaWorksheetDto>
  currentCreditApplication: CreditApplication
  worksheet: EditAutoRslaWorksheetDto
  reset: UseFormReset<EditAutoRslaWorksheetDto>
  register: UseFormRegister<EditAutoRslaWorksheetDto>
  watch: UseFormWatch<EditAutoRslaWorksheetDto>
  errors: Merge<FieldError, FieldErrorsImpl<EditAutoRslaWorksheetDto>> | undefined
  creditApplicationInterestRate: number
  minFirstPaymentDate: Date
  maxFirstPaymentDate: Date
}

const TransactionForm = ({
  control,
  worksheet,
  reset,
  register,
  errors,
  creditApplicationInterestRate,
  minFirstPaymentDate,
  maxFirstPaymentDate,
}: Props) => {
  const { t } = useTranslation()

  useEffect(() => {
    const today = formatDate(new Date())
    if (worksheet) {
      reset({
        ...worksheet,
        status: EditAutoRslaWorksheetDtoSchema.getDefault().status,
        deliveryOn: formatDate(worksheet.deliveryOn) < today ? today : worksheet.deliveryOn,
      })
    }
  }, [worksheet, reset])

  const termOptions = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return [...Array(13)].map((_, i) => {
      const item = 6 * (i + 1) + 6
      return {
        label: `${item} ${t('worksheet.month')}`,
        value: item,
      }
    })
  }, [t])
  const paymentFrequencyList = useMemo(() => {
    return Constants.financingProgramConfigs[EFinancingProgram.CreditRsla].supportedPaymentFrequencies.map((item) => {
      const data: SelectValueListItem = { label: t(`EPaymentFrequency.${item}`, { ns: 'enum' }), value: item }
      return data
    })
  }, [t])

  return (
    <>
      <Divider style={{ fontSize: 25 }}>
        <Typography variant="h4" component="span" gutterBottom>
          Transaction
        </Typography>
      </Divider>
      <Grid container spacing={3} sx={{ mt: 1, p: 2 }} size={12}>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            disabled
            label={t('worksheetCommon.amountRequested')}
            error={errors?.amountRequested}
            {...register('amountRequested')}
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 3,
          }}
        >
          <InputTextField
            label={t('worksheet.lienAmount')}
            {...register(`lienAmountFee`)}
            error={errors?.lienAmountFee}
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 3,
          }}
        >
          <InputTextField
            label={t('worksheet.brokerAdminFee')}
            {...register(`brokerAdminFee`)}
            error={errors?.brokerAdminFee}
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 3,
          }}
        >
          <InputTextField
            label={t('worksheet.roadside')}
            {...register(`roadsideAssistanceAmount`)}
            error={errors?.roadsideAssistanceAmount}
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 3,
          }}
        >
          <InputTextField
            label={t('worksheet.merchantReferralFee')}
            {...register(`merchantReferralFee`)}
            error={errors?.merchantReferralFee}
          />
        </Grid>
        <Grid
          sx={{ flexDirection: 'row' }}
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <ControlledStringDateOnlyPicker
            name="deliveryOn"
            control={control}
            label={t('worksheet.deliveryOn')}
            disablePast
            fullWidth
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 3,
          }}
        >
          <InputTextField
            label={t('worksheet.protectionPlan')}
            {...register(`insuranceFee`)}
            error={errors?.insuranceFee}
          />
        </Grid>
      </Grid>
      <Typography variant="h6" component="div" sx={{ paddingLeft: 2 }} gutterBottom>
        {t('worksheet.funding')}
      </Typography>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }} size={12}>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            label={t('worksheet.paymentAmount')}
            error={errors?.paymentAmountForFrequency}
            {...register('paymentAmountForFrequency')}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <SelectComponent items={termOptions} label={t('worksheet.term')} {...register('term')} error={errors?.term} />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <SelectComponent
            items={paymentFrequencyList}
            label={t('worksheet.paymentFrequency')}
            {...register('paymentFrequency')}
            error={errors?.paymentFrequency}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField disabled value={creditApplicationInterestRate} label={t('worksheet.interestRate')} />
        </Grid>

        <Grid
          sx={{ flexDirection: 'row' }}
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <ControlledStringDateOnlyPicker
            name="firstPaymentOn"
            control={control}
            label={t('worksheet.firstPaymentOn')}
            minDate={minFirstPaymentDate}
            maxDate={maxFirstPaymentDate}
            fullWidth
          />
        </Grid>
      </Grid>
    </>
  )
}

export default TransactionForm
